import {WebviewUtils} from '@shared/webviewUtils';
import {ROUTE_PATHS} from '@di-shared/routes';
import {UAParser} from 'ua-parser-js';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {diApi} from '@di-shared/api/di';
import {UserOsType} from '@shared/cci/features/const/environment';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';

enum CapitalMessageEventTypes {
  // response
  getAccessToken = 'getAccessToken',
  showAlert = 'showAlert',
  getCbno = 'getCbno',
  resizeIframe = 'resizeIframe',
  mydataMounted = 'mydataMounted',
  openNewWebView = 'openNewWebView',
  openNewIframeWebView = 'openNewIframeWebView',
  closeIframeWebView = 'closeIframeWebView',
  refreshToken = 'refreshToken',
  callAuth = 'callAuth',
  // request
  androidBackClicked = 'androidBackClicked',
  executeOnFinished = 'executeOnFinished',
  changeDarkMode = 'changeDarkMode',
  changeCbno = 'changeCbno',
}

enum WebviewIds {
  INSURANCE = 'INSURANCE',
  LOAN = 'LOAN',
  MAIN = 'MAIN',
}

/**
 * 사설 인증 타입
 * 네이버 | 토스 | 카카오
 */
enum ExternalCertificationType {
  NAVR = 'NAVR',
  TOSS = 'TOSS',
  KKAO = 'KKAO',
}

const STORE_URL = {
  [UserOsType.IOS]: {
    [ExternalCertificationType.NAVR]: 'https://itunes.apple.com/app/id393499958',
    [ExternalCertificationType.TOSS]: 'https://apps.apple.com/app/id839333328',
    [ExternalCertificationType.KKAO]: 'https://itunes.apple.com/app/id362057947',
  },
  [UserOsType.AOS]: {
    [ExternalCertificationType.NAVR]: 'market://details?id=com.nhn.android.search',
    [ExternalCertificationType.TOSS]: 'market://details?id=viva.republica.toss',
    [ExternalCertificationType.KKAO]: 'market://details?id=com.kakao.talk',
  },
};

export function getCapitalMessage(options: {
  iframeEl: HTMLIFrameElement;
  vin: string;
  origin: string;
  invalidate: Function;
  onMounted: Function;
}) {
  const {appVersion, ccid, puid, os} = environmentStore.getState();
  const parser = new UAParser(window.navigator.userAgent);
  const brand = getHmcType();

  const requests = {
    /**
     * API ID	androidBackClicked		API 이름	안드로이드 Back 이벤트 전달
     * API 제공자	현대캐피탈 Web		API 요청자	현대자동차 Web
     * API 명 (URI)			요청방식	postMessage
     * 버전			변경사항
     * 설명	"안드로이드 Back 이벤트 전달
     * history stack 존재하는 경우 뒤로가기, 존재하지 않는 경우 exitCapitalPage 인터페이스 호출
     */
    [CapitalMessageEventTypes.androidBackClicked]() {
      requestToIframe({
        name: CapitalMessageEventTypes.androidBackClicked,
        webViewID: WebviewIds.MAIN,
      });
    },
    /**
     * API ID	executeOnFinished		API 이름	Callback 함수 호출
     * API 제공자	현대캐피탈 Web		API 요청자	현대자동차 Web
     * API 명 (URI)			요청방식	postMessage
     * 버전			변경사항
     * 설명	현대캐피탈에서 현대자동차 Web에 postMessage 호출 후 Callback이 필요한 경우 호출
     *
     * @param webViewID
     * @param callbackFunc
     * @param callbackData
     */
    [CapitalMessageEventTypes.executeOnFinished]<T>(webViewID: WebviewIds, callbackFunc: string, callbackData: T) {
      requestToIframe({
        name: CapitalMessageEventTypes.executeOnFinished,
        webViewID,
        callbackFunc,
        callbackData,
      });
    },
    /**
     * API ID	changeDarkMode		API 이름	색상모드 변경
     * API 제공자	현대캐피탈 Web		API 요청자	현대자동차 Web
     * API 명 (URI)			요청방식	postMessage
     * 버전			변경사항
     * 설명	현대자동차 Web에서 색상모드(다크/라이트모드) 변경 시 호출
     *
     * @param webViewID
     * @param isLightMode
     */
    [CapitalMessageEventTypes.changeDarkMode](webViewID: WebviewIds, isLightMode: boolean) {
      requestToIframe({
        name: CapitalMessageEventTypes.changeDarkMode,
        webViewID,
        isLightMode,
      });
    },
    /**
     * API ID	changeCbno		API 이름	차량 변경
     * API 제공자	현대캐피탈 Web		API 요청자	현대자동차 Web
     * API 명 (URI)			요청방식	postMessage
     * 버전			변경사항
     * 설명	현대자동차 Web에서 선택 차량 변경 시 호출
     */
    [CapitalMessageEventTypes.changeCbno](webViewID: WebviewIds, vin: string) {
      requestToIframe({
        name: CapitalMessageEventTypes.changeCbno,
        webViewID,
        cbno: vin,
      });
    },
  };

  function requestToIframe(message: any) {
    if (!options.iframeEl.contentWindow) {
      return;
    }
    options.iframeEl.contentWindow.postMessage(message, options.origin);
  }

  async function messageListener(event: MessageEvent) {
    if (event.origin !== options.origin) {
      return;
    }
    if (event.data.name === CapitalMessageEventTypes.getAccessToken) {
      /**
       * API ID	getAccessToken		API 이름	JWT 토큰 요청
       * API 제공자	현대자동차 Web		API 요청자	현대캐피탈 Web
       * API 명 (URI)			요청방식	postMessage
       * 버전			변경사항
       * 설명	"AccessToken 요청
       * 캐피탈 마이데이터 화면 초기 진입 시 호출
       * 만료 시 갱신된 AccessToken 반환 필요"
       *
       */
      try {
        // 사용자 IP 를 알기 위해, gw를 통하지 않게 호출하여 사용자의 ip 를 추출
        const {data: tokenData} = await diApi.financialTokenManagementController.issueFinancialToken({
          requestBody: {
            vin: options.vin,
            puid,
            osVersion: parser.getOS().version || '',
            osType: parser.getOS().name || '',
            appVersion: appVersion || '',
            model: parser.getDevice().model || '',
            uuid: (ccid || '').split('_')[0],
          },
        });
        requests[CapitalMessageEventTypes.executeOnFinished](event.data.webViewID, event.data.onFinished, {
          rspCode: '00000', // 성공: "00000" (그 외 실패)
          rspMsg: 'success',
          hmcType: brand,
          accessToken: tokenData.access_token,
          expiresIn: tokenData.expires_in, // 단위: 초
          tokenType: 'Bearer', // 고정값: "Bearer
        });
      } catch (e: any) {
        options.invalidate();
        requests[CapitalMessageEventTypes.executeOnFinished](event.data.webViewID, event.data.onFinished, {
          rspCode: '90000', // 성공: "00000" (그 외 실패)
          rspMsg: e.message || '',
          hmcType: brand,
          accessToken: '',
          expiresIn: 0, // 단위: 초
          refreshToken: '',
          refreshTokenExpiresIn: 0, // 단위: 초
          tokenType: 'Bearer', // 고정값: "Bearer
        });
      }
    } else if (event.data.name === CapitalMessageEventTypes.showAlert) {
      /**
       * API ID	showAlert		API 이름	Alert 팝업 노출
       * API 제공자	현대자동차 Web		API 요청자	현대캐피탈 Web
       * API 명 (URI)			요청방식	postMessage
       * 버전			변경사항
       * 설명	Alert 팝업 노출 이후 버튼 클릭시 Callback 실행
       *
       * 혹시 몰라서 || 방어
       */
      await WebviewUtils.openCustomAlert({
        title: event.data.title || '',
        message: event.data.message || '',
        buttons: [{text: event.data.confirmText || '확인', style: 'PRIMARY', actionType: 'DEFAULT'}],
      });
      requests[CapitalMessageEventTypes.executeOnFinished](event.data.webViewID, event.data.onFinished, null);
    } else if (event.data.name === CapitalMessageEventTypes.getCbno) {
      /**
       * API ID	getCbno		API 이름	차대번호 요청
       * API 제공자	현대자동차 Web		API 요청자	현대캐피탈 Web
       * API 명 (URI)			요청방식	postMessage
       * 버전			변경사항
       * 설명	차대번호 요청
       *
       */
      requests[CapitalMessageEventTypes.executeOnFinished](event.data.webViewID, event.data.onFinished, {
        cbno: options.vin,
      });
    } else if (event.data.name === CapitalMessageEventTypes.resizeIframe) {
      /**
       * API ID	resizeIframe		API 이름	iframe 사이즈변경
       * API 제공자	현대자동차 Web		API 요청자	현대캐피탈 Web
       * API 명 (URI)			요청방식	postMessage
       * 버전			변경사항
       * 설명	보험, 대출 컴포넌트에서 데이터 수신 이후 UI 변경으로 인한 사이즈 변경으로 스크롤 방지를 위해 부모창에서 iframe size 변경 필요
       *
       */
      if (event.data.width) {
        options.iframeEl.style.width = `${event.data.width}px`;
      }
      if (event.data.height) {
        options.iframeEl.style.height = `${event.data.height}px`;
      }
    } else if (event.data.name === CapitalMessageEventTypes.mydataMounted) {
      /**
       * API ID	mydataMounted	HTTP Method	API 이름	iframe 마운트
       * API 제공자	현대자동차 Web	API 요청자	API 요청자	현대캐피탈 Web
       * API 명 (URI)			요청방식	postMessage
       * 버전			변경사항
       * 설명	"iframe URL 접속 이후 다크/라이트 모드 세팅 이전에 흰화면이 노출되는것을 방지하고자 mount 완료 여부 송신
       * (해당 인터페이스 수신 이후 display: none 제거 필요)"
       *
       */
      options.onMounted();
      options.iframeEl.style.display = `block`;
    } else if (event.data.name === CapitalMessageEventTypes.openNewWebView) {
      /**
       * API ID	openNewWebView		API 이름	WebView open 요청
       * API 제공자	현대자동차 Web		API 요청자	현대캐피탈 Web
       * API 명 (URI)			요청방식	postMessage
       * 버전			변경사항
       * 설명	"WebView Open 요청 시 window.open처럼 동작
       * 보험 배너 클릭 후 현대차 페이지 이동 시 사용"
       *
       */
      WebviewUtils.openNewWebView({
        url: event.data.link,
        isModal: true,
        isNavigationBar: false,
        isToolBar: false,
      });
    } else if (event.data.name === CapitalMessageEventTypes.openNewIframeWebView) {
      /**
       * API ID	openNewIframeWebView		API 이름	Iframe WebView open 요청
       * API 제공자	현대자동차 Web		API 요청자	현대캐피탈 Web
       * API 명 (URI)			요청방식	postMessage
       * 버전			변경사항
       * 설명	"보험, 대출 컴포넌트에서 버튼 클릭 시 full size iframe 페이지로 이동
       * (신규 웹뷰 -> 현대차 웹 -> 캐피탈 Iframe)"
       *
       */
      WebviewUtils.openNewWebView({
        url: window.location.origin + ROUTE_PATHS.CAPITAL_FULL_PAGE.getFullPath(options.vin, event.data.link),
        isModal: true,
        isNavigationBar: false,
        isToolBar: false,
      });
    } else if (event.data.name === CapitalMessageEventTypes.closeIframeWebView) {
      /**
       * API ID	closeIframeWebView		API 이름	Iframe WebView close 요청
       * API 제공자	현대자동차 Web		API 요청자	현대캐피탈 Web
       * API 명 (URI)			요청방식	postMessage
       * 버전			변경사항
       * 설명	full size iframe 페이지에서 닫기 버튼 클릭 시 웹뷰 닫기
       *
       */
      WebviewUtils.close();
    } else if (event.data.name === CapitalMessageEventTypes.callAuth) {
      /**
       * API ID	callAuth	HTTP Method	API 이름	사설 인증 요청
       * API 제공자	현대자동차 Web	API 요청자	API 요청자	현대캐피탈 Web
       * API 명 (URI)			요청방식	postMessage
       * 버전			변경사항
       * 설명	현대캐피탈 Web에서 자산 연동 시 본인 인증을 위한 사설 인증 요청
       *
       */
      const authType: ExternalCertificationType = event.data.authType || ExternalCertificationType.KKAO;
      let response;
      if (os === UserOsType.IOS) {
        const storeUrl = STORE_URL[UserOsType.IOS][authType];
        response = await ccWebviewUtils.callExternalCertification(event.data.iosAppUri, storeUrl);
      } else {
        const storeUrl = STORE_URL[UserOsType.AOS][authType];
        response = await ccWebviewUtils.callExternalCertification(event.data.androidAppUri, storeUrl);
      }
      let errorMessage = '인증 실패';
      if (response.errorMessage) {
        // 네이버의 경우에만 상태를 줘서 에러 메시지를 내려주기로 함 success | cancel | fail
        if (response.errorMessage === 'cancel') {
          errorMessage = '인증 취소';
        } else if (response.errorMessage === 'fail') {
          errorMessage = '인증 실패';
        } else {
          errorMessage = response.errorMessage;
        }
      }
      requests[CapitalMessageEventTypes.executeOnFinished](event.data.webViewID, event.data.onFinished, {
        result: response.isSuccess ? 'true' : 'false', // 성공: "true", 실패: "false"
        errorCode: response.isSuccess ? '' : '-1', // 성공: "", 실패: "-1"
        errorMessage: response.isSuccess ? '' : errorMessage, // 성공: "", 실패: "인증 실패", 취소: "인증 취소"
      });
    } else {
      // do nothing
    }
  }

  function listenCapitalMessage() {
    window.addEventListener('message', messageListener);
  }

  function releaseCapitalMessage() {
    window.removeEventListener('message', messageListener);
  }

  return {
    requests,
    listenCapitalMessage,
    releaseCapitalMessage,
  };
}

export function getHmcType() {
  return import.meta.env.VITE_BRAND.slice(0, 1); // return K/H/G
}
